import NetworkNode from '../../lib/NetworkNode';
import { formatWhen } from '../../lib/badgeTimes';
import { lowBatteryPercent } from '../../constants';
import { T, INDICATOR, INDICATOR_TEXT } from '../../constants/tokens';

/**
 * How the network screens describe a node. Shared by the Network page, the
 * Gateways and Anchors device tabs, and the node detail panel, so a node reads
 * the same wherever it appears.
 */

/** Below this link quality a node counts as a weak point. */
export const WEAK_QUALITY = 75;

const BAND_TEXT = {
  strong: INDICATOR_TEXT.success,
  good: INDICATOR_TEXT.accent,
  marginal: INDICATOR_TEXT.warning,
  weak: INDICATOR_TEXT.error
};

const BAND_FILL = {
  strong: INDICATOR.success,
  good: INDICATOR.accent,
  marginal: INDICATOR.warning,
  weak: INDICATOR.error
};

/** Text colour for a link quality percentage. */
export const qualityText = (quality) => {
  const band = NetworkNode.qualityBand(quality);
  return band ? BAND_TEXT[band.key] : T.body;
};

/** Dot or bar colour for a link quality percentage. */
export const qualityFill = (quality) => {
  const band = NetworkNode.qualityBand(quality);
  return band ? BAND_FILL[band.key] : T.mutedSoft;
};

export const QUALITY_LEGEND = [
  { label: '90–100% strong', color: BAND_FILL.strong },
  { label: '75–89% good', color: BAND_FILL.good },
  { label: '65–74% marginal', color: BAND_FILL.marginal },
  { label: '< 65% or off mesh', color: BAND_FILL.weak }
];

/**
 * Online, off the mesh, or not reported. Gateways are "unreachable" rather
 * than "off mesh", as the design words it.
 */
export const statusOf = (node) => {
  const connected = node.getIsConnected();
  if (connected === true) return { label: 'Online', dot: INDICATOR.success, text: T.body };
  if (connected === false) {
    return {
      label: node.isGateway() ? 'Unreachable' : 'Off mesh',
      dot: INDICATOR.error,
      text: INDICATOR_TEXT.error
    };
  }
  return { label: 'Not reported', dot: T.mutedSoft, text: T.muted };
};

/** A node is a weak point when its link is poor or it has dropped off. */
export const isWeak = (node) => !node.isGateway() && (
  node.getIsConnected() === false ||
  (node.getNextHopQuality() != null && node.getNextHopQuality() < WEAK_QUALITY)
);

export const formatQuality = (node) => {
  if (node.isGateway()) return node.getIsConnected() === false ? '—' : '100%';
  return node.getNextHopQuality() == null ? '—' : Math.round(node.getNextHopQuality()) + '%';
};

export const formatRssi = (node) => (
  node.isGateway() || node.getNextHopRssi() == null ? '—' : Math.round(node.getNextHopRssi()) + ' dBm'
);

export const formatLatency = (node) => (node.getLatencyMs() == null ? '—' : node.getLatencyMs() + ' ms');

/** Latency over this reads as slow, as the design colours it. */
export const SLOW_LATENCY_MS = 150;

export const latencyText = (node) => {
  if (node.getLatencyMs() == null) return T.muted;
  return node.getLatencyMs() > SLOW_LATENCY_MS ? INDICATOR_TEXT.warning : T.body;
};

/** "3.10 V · 19%", or "mains" for a gateway. */
export const formatBattery = (node) => {
  if (node.isGateway()) return 'mains';
  const voltage = node.getVoltage();
  if (voltage == null || voltage <= 0) return '—';
  const percent = node.getBatteryPercent();
  return voltage.toFixed(2) + ' V' + (percent == null ? '' : ' · ' + percent + '%');
};

export const batteryText = (node) => {
  if (node.isGateway()) return T.muted;
  const percent = node.getBatteryPercent();
  return percent != null && percent < lowBatteryPercent() ? INDICATOR_TEXT.error : T.body;
};

/** Time today, or date and time when it was not today. */
export const formatSeen = (node) => formatWhen(node.getLastSeenTimestamp()) ?? '—';

/** What to call the node under its address. */
export const describe = (node) => {
  // The server already names hubs and badges by what they are ("Room hub ·
  // Room 120", "Badge · R. Alvarez"), so the kind would only repeat itself.
  const devices = [NetworkNode.KINDS.HUB, NetworkNode.KINDS.BADGE];
  if (devices.includes(node.getKind()) && node.getLabel()) return node.getLabel();

  const role = node.getKind() === NetworkNode.KINDS.ANCHOR
    ? (node.getModel() ?? '') + ' anchor'
    : node.getKind();
  return [role.trim(), node.getLabel()].filter(Boolean).join(' · ');
};
